import moment from 'moment'
import { DEFAULT_DATE_FORMAT_WITHOUT_TIME } from '@/utils/dateUtils'
import { count, text } from './constants'

export type ParsedSearch = {
  /** Free text bound for `SearchValue` — name, MRN, phone or email. */
  searchStringCondition: string
  /** `BirthDate` in the format the patients endpoint expects, or '' when none was typed. */
  birthDate: string
  /** The same date in MM/DD/YYYY, for showing the caller how their input was read. */
  displayDate: string
  /** A token shaped like a date that could not be one, verbatim, or '' when there is none. */
  invalidDate: string
  /** Whether this input is worth a request. */
  isSearchable: boolean
}

/**
 * A token is only considered for a date of birth when it carries explicit
 * separators and a four-digit year on one end. A bare digit run such as
 * `05141982` stays free text so a medical record number is never silently
 * reinterpreted as a date, and a three-group phone number (`555-123-4567`)
 * cannot match at all.
 */
const DOB_CANDIDATE_PATTERN = /^(?:\d{4}[/.-]\d{1,2}[/.-]\d{1,2}|\d{1,2}[/.-]\d{1,2}[/.-]\d{4})$/

// Parsed strictly, so out-of-range months and days are rejected rather than
// rolled over. A leading four-digit group is read as ISO order, anything else
// as US month-first order.
const DOB_FORMATS = ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY/MM/DD', 'YYYY/M/D']

const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY'

type DobToken = { birthDate: string; displayDate: string }

/**
 * Returns the parsed date, the string `'invalid'` for a token that is shaped like
 * a date but cannot be one, or undefined for a token that was never a date
 * attempt. The middle case is what lets the caller explain itself: a half-typed
 * `05/14/198` is indistinguishable from free text and stays free text, but a
 * complete `13/45/1982` is a typo worth reporting.
 */
const parseDobToken = (token: string): DobToken | 'invalid' | undefined => {
  if (!DOB_CANDIDATE_PATTERN.test(token)) {
    return undefined
  }

  const parsed = moment(token.replace(/[.-]/g, '/'), DOB_FORMATS, true)
  if (!parsed.isValid() || parsed.isBefore(text.MIN_VALID_DOB) || parsed.isAfter(moment())) {
    return 'invalid'
  }

  return {
    birthDate: parsed.format(DEFAULT_DATE_FORMAT_WITHOUT_TIME),
    displayDate: parsed.format(DISPLAY_DATE_FORMAT),
  }
}

/**
 * Splits the single search box into the two parameters the patients endpoint
 * accepts. A date found anywhere in the input becomes `birthDate`; everything
 * left over becomes `searchStringCondition`, so a name (or MRN, phone, email)
 * and a date of birth can be searched together. Either half may stand alone.
 *
 * `isSearchable` is false when there is nothing worth searching for — no date,
 * and less free text than `SEARCH_VALUE_MIN_LENGTH` — or when a date token was
 * typed that cannot be a date. A date is enough on its own, and narrows the
 * result set enough that the free-text minimum does not apply alongside it.
 */
export const buildSearchQuery = (value: string): ParsedSearch => {
  const tokens = value
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean)

  let birthDate = ''
  let displayDate = ''
  let invalidDate = ''
  const searchTokens: string[] = []

  tokens.forEach((token) => {
    const parsed = birthDate ? undefined : parseDobToken(token)

    if (parsed === 'invalid') {
      if (!invalidDate) {
        invalidDate = token
      }
    } else if (parsed) {
      birthDate = parsed.birthDate
      displayDate = parsed.displayDate
    } else {
      searchTokens.push(token)
    }
  })

  const searchStringCondition = searchTokens.join(' ')
  const hasEnoughText = searchStringCondition.length >= count.SEARCH_VALUE_MIN_LENGTH
  const isSearchable = !invalidDate && (Boolean(birthDate) || hasEnoughText)

  return { searchStringCondition, birthDate, displayDate, invalidDate, isSearchable }
}
